import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import * as XLSX from "xlsx";
import { getAiDrinks } from "../../../../services/Admin/AiDrinksApi";
import { getReportsLogs } from "../../../../services/Admin/ReportsApi";
import { getMenu } from "../../../../services/Admin/SalesScreenApi";
import {
  OrderLogTableHead,
  OrderLogTableRow,
  actionConfig,
  buildOrderLogExportRows,
  createOrderLogColumns,
  getOrderLogSearchText,
  modelTypeLabel,
  orderTypeLabel,
} from "./OrderLogReportColumns";

const headSx = {
  fontWeight: 700,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  whiteSpace: "nowrap",
};

const COLUMN_SETTINGS_STORAGE_KEY = "nuvvy_order_logs_hidden_columns";

const getTodayDateInput = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseTimeInput = (value, fallback) => {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return fallback;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return fallback;

  return { hours, minutes };
};

const buildFilterDateTime = (dateValue, timeValue, fallbackTime) => {
  if (!dateValue) return null;

  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) return null;

  const parsedTime = parseTimeInput(timeValue, fallbackTime);
  const date = new Date(year, month - 1, day, parsedTime.hours, parsedTime.minutes, 0, 0);

  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const getOrderNumberKey = (log) => {
  const orderNumber = log?.order?.general_increment ?? log?.order?.id ?? log?.order_id;
  return orderNumber === null || orderNumber === undefined || orderNumber === "" ? null : String(orderNumber);
};

const isDeleteOrUpdateLog = (log) => log?.action === "updated" || log?.action === "deleted";

const getSavedHiddenColumns = () => {
  try {
    const saved = localStorage.getItem(COLUMN_SETTINGS_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const buildProductNamesMap = (menuData) => {
  const map = {};
  if (!Array.isArray(menuData)) return map;

  menuData.forEach((category) => {
    if (!Array.isArray(category?.products)) return;
    category.products.forEach((product) => {
      if (!product?.id) return;
      map[String(product.id)] = product.name || product.name_en || String(product.id);
    });
  });

  return map;
};

const buildDrinkNamesMap = (drinksData) => {
  const map = {};
  if (!Array.isArray(drinksData)) return map;

  drinksData.forEach((drink) => {
    if (!drink?.id) return;
    map[String(drink.id)] = drink.drink_name || drink.name || drink.name_en || String(drink.id);
  });

  return map;
};

const OrdersLogTap = () => {
  const [logs, setLogs] = useState([]);
  const [productNamesById, setProductNamesById] = useState({});
  const [drinkNamesById, setDrinkNamesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(getTodayDateInput);
  const [toDate, setToDate] = useState(getTodayDateInput);
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [userName, setUserName] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [modelTypeFilter, setModelTypeFilter] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("");
  const [mostChangedOrdersOnly, setMostChangedOrdersOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState(getSavedHiddenColumns);

  const columns = useMemo(() => (
    createOrderLogColumns({ productNamesById, drinkNamesById })
  ), [productNamesById, drinkNamesById]);

  const hiddenColumnKeySet = useMemo(() => new Set(hiddenColumnKeys), [hiddenColumnKeys]);

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((column) => !hiddenColumnKeySet.has(column.key));
    return visible.length ? visible : columns;
  }, [columns, hiddenColumnKeySet]);

  const visibleColumnKeySet = useMemo(() => (
    new Set(visibleColumns.map((column) => column.key))
  ), [visibleColumns]);

  useEffect(() => {
    localStorage.setItem(COLUMN_SETTINGS_STORAGE_KEY, JSON.stringify(hiddenColumnKeys));
  }, [hiddenColumnKeys]);

  const userNames = useMemo(() => {
    const names = logs
      .map((log) => log.user?.name)
      .filter(Boolean);
    return [...new Set(names)].sort();
  }, [logs]);

  const actionOptions = useMemo(() => {
    const actions = logs.map((log) => log.action).filter(Boolean);
    return [...new Set(actions)].sort();
  }, [logs]);

  const modelTypeOptions = useMemo(() => {
    const types = logs.map((log) => log.model_type).filter(Boolean);
    return [...new Set(types)].sort();
  }, [logs]);

  const orderTypeOptions = useMemo(() => {
    const types = logs.map((log) => log.order?.order_type).filter(Boolean);
    return [...new Set(types)].sort();
  }, [logs]);

  const dateFilteredLogs = useMemo(() => {
    const fromDateTime = buildFilterDateTime(fromDate, fromTime, { hours: 0, minutes: 0 });
    const toDateTime = buildFilterDateTime(toDate, toTime, { hours: 23, minutes: 59 });
    const toDateTimeEnd = toDateTime === null ? null : toDateTime + 59999;

    return logs.filter((log) => {
      const logDateTime = log.created_at ? new Date(log.created_at).getTime() : null;

      if (fromDateTime !== null && (logDateTime === null || Number.isNaN(logDateTime) || logDateTime < fromDateTime)) return false;
      if (toDateTimeEnd !== null && (logDateTime === null || Number.isNaN(logDateTime) || logDateTime > toDateTimeEnd)) return false;

      return true;
    });
  }, [logs, fromDate, fromTime, toDate, toTime]);

  const frequentOrderStats = useMemo(() => {
    const stats = new Map();

    dateFilteredLogs.forEach((log) => {
      if (!isDeleteOrUpdateLog(log)) return;

      const orderKey = getOrderNumberKey(log);
      if (!orderKey) return;

      const current = stats.get(orderKey) || {
        count: 0,
        lastCreatedAt: "",
      };

      stats.set(orderKey, {
        count: current.count + 1,
        lastCreatedAt: log.created_at > current.lastCreatedAt ? log.created_at : current.lastCreatedAt,
      });
    });

    return stats;
  }, [dateFilteredLogs]);

  const frequentOrderKeys = useMemo(() => (
    [...frequentOrderStats.entries()]
      .sort((a, b) => {
        const countDiff = b[1].count - a[1].count;
        if (countDiff !== 0) return countDiff;
        return String(a[0]).localeCompare(String(b[0]), "ar");
      })
      .map(([orderKey]) => orderKey)
  ), [frequentOrderStats]);

  const frequentOrderRank = useMemo(() => (
    frequentOrderKeys.reduce((rankMap, orderKey, index) => {
      rankMap.set(orderKey, index);
      return rankMap;
    }, new Map())
  ), [frequentOrderKeys]);

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();

    const rows = dateFilteredLogs.filter((log, index) => {
      const orderKey = getOrderNumberKey(log);

      if (mostChangedOrdersOnly && (!isDeleteOrUpdateLog(log) || !orderKey || !frequentOrderStats.has(orderKey))) return false;
      if (userName && log.user?.name !== userName) return false;
      if (actionFilter && log.action !== actionFilter) return false;
      if (modelTypeFilter && log.model_type !== modelTypeFilter) return false;
      if (orderTypeFilter && log.order?.order_type !== orderTypeFilter) return false;
      if (q && !getOrderLogSearchText(log, columns, index).includes(q)) return false;

      return true;
    });

    if (!mostChangedOrdersOnly) return rows;

    return [...rows].sort((a, b) => {
      const aKey = getOrderNumberKey(a);
      const bKey = getOrderNumberKey(b);
      const rankDiff = (frequentOrderRank.get(aKey) ?? 0) - (frequentOrderRank.get(bKey) ?? 0);
      if (rankDiff !== 0) return rankDiff;
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [
    dateFilteredLogs,
    columns,
    userName,
    actionFilter,
    modelTypeFilter,
    orderTypeFilter,
    mostChangedOrdersOnly,
    frequentOrderStats,
    frequentOrderRank,
    search,
  ]);

  const handleClearFilter = () => {
    const today = getTodayDateInput();

    setFromDate(today);
    setToDate(today);
    setFromTime("00:00");
    setToTime("23:59");
    setUserName("");
    setActionFilter("");
    setModelTypeFilter("");
    setOrderTypeFilter("");
    setMostChangedOrdersOnly(false);
    setSearch("");
  };

  const handleExport = () => {
    const rows = buildOrderLogExportRows(filteredLogs, visibleColumns);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "تقارير الطلبات");
    XLSX.writeFile(wb, `تقارير_الطلبات_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleToggleColumn = (columnKey) => {
    setHiddenColumnKeys((current) => {
      const hiddenSet = new Set(current);
      const isHidden = hiddenSet.has(columnKey);

      if (isHidden) {
        hiddenSet.delete(columnKey);
      } else {
        const visibleCount = columns.filter((column) => !hiddenSet.has(column.key)).length;
        if (visibleCount <= 1) return current;
        hiddenSet.add(columnKey);
      }

      return [...hiddenSet].filter((key) => columns.some((column) => column.key === key));
    });
  };

  const handleShowAllColumns = () => {
    setHiddenColumnKeys([]);
  };

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const [logsResult, menuResult, drinksResult] = await Promise.allSettled([
          getReportsLogs(),
          getMenu(),
          getAiDrinks(),
        ]);

        if (logsResult.status === "rejected") {
          throw logsResult.reason;
        }

        setLogs(logsResult.value?.data || []);

        if (menuResult.status === "fulfilled") {
          setProductNamesById(buildProductNamesMap(menuResult.value?.data));
        } else {
          setProductNamesById({});
        }

        if (drinksResult.status === "fulfilled") {
          setDrinkNamesById(buildDrinkNamesMap(drinksResult.value?.data));
        } else {
          setDrinkNamesById({});
        }
      } catch (err) {
        setError(err.message || "حدث خطأ أثناء تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (logs.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        لا توجد سجلات متاحة
      </Alert>
    );
  }

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        placeholder="بحث في كل البيانات..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
        }}
        sx={{ mb: 2 }}
      />

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" fontWeight={700} color="text.secondary">
          تصفية:
        </Typography>
        <TextField
          label="من"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: toDate || undefined }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="من وقت"
          type="time"
          size="small"
          value={fromTime}
          onChange={(e) => setFromTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ width: 125 }}
        />
        <TextField
          label="إلى"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: fromDate || undefined }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="إلى وقت"
          type="time"
          size="small"
          value={toTime}
          onChange={(e) => setToTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ width: 125 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>المستخدم</InputLabel>
          <Select
            value={userName}
            label="المستخدم"
            onChange={(e) => setUserName(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {userNames.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>العملية</InputLabel>
          <Select
            value={actionFilter}
            label="العملية"
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {actionOptions.map((action) => (
              <MenuItem key={action} value={action}>{actionConfig[action]?.label || action}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>نوع السجل</InputLabel>
          <Select
            value={modelTypeFilter}
            label="نوع السجل"
            onChange={(e) => setModelTypeFilter(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {modelTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>{modelTypeLabel[type] || type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>نوع الطلب</InputLabel>
          <Select
            value={orderTypeFilter}
            label="نوع الطلب"
            onChange={(e) => setOrderTypeFilter(e.target.value)}
          >
            <MenuItem value="">الكل</MenuItem>
            {orderTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>{orderTypeLabel[type] || type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(fromDate || toDate || userName || actionFilter || modelTypeFilter || orderTypeFilter || mostChangedOrdersOnly || search) && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<FilterAltOffIcon />}
            onClick={handleClearFilter}
          >
            إلغاء الفلتر
          </Button>
        )}
        <Button
          size="small"
          variant={mostChangedOrdersOnly ? "contained" : "outlined"}
          color={mostChangedOrdersOnly ? "warning" : "inherit"}
          startIcon={<FilterListIcon />}
          onClick={() => {
            setMostChangedOrdersOnly((value) => !value);
            setActionFilter("");
          }}
        >
          فلتر الطلبات الاكثر حذف وتعديل
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          يُعرض {filteredLogs.length} من {logs.length} سجل | {visibleColumns.length} من {columns.length} عمود
          {mostChangedOrdersOnly ? ` | ${frequentOrderKeys.length} طلب` : ""}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<SettingsIcon />}
          onClick={() => setSettingsOpen(true)}
        >
          إعدادات الأعمدة
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={filteredLogs.length === 0}
        >
          تصدير Excel
        </Button>
      </Paper>

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>إعدادات الأعمدة</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            اختر الأعمدة التي تريد ظهورها في الجدول والتصدير.
          </Typography>
          <FormGroup
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 0.5,
            }}
          >
            {columns.map((column) => {
              const checked = visibleColumnKeySet.has(column.key);
              const disableHide = checked && visibleColumns.length <= 1;

              return (
                <FormControlLabel
                  key={column.key}
                  control={(
                    <Checkbox
                      checked={checked}
                      onChange={() => handleToggleColumn(column.key)}
                      disabled={disableHide}
                    />
                  )}
                  label={column.label}
                  sx={{
                    m: 0,
                    px: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                />
              );
            })}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button color="inherit" onClick={handleShowAllColumns}>
            إظهار الكل
          </Button>
          <Button variant="contained" onClick={() => setSettingsOpen(false)}>
            تم
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: "70vh" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <OrderLogTableHead columns={visibleColumns} headSx={headSx} />
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  لا توجد سجلات في هذا النطاق الزمني
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map((log, index) => (
                <OrderLogTableRow key={log.id} log={log} columns={visibleColumns} rowIndex={index} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrdersLogTap;
